// Title row for scheduled calendar blocks — title priority + responsive project pill tiers.
import { useMemo, useRef } from 'react';
import { Box, Checkbox } from '@mui/material';
import {
  planTaskCheckboxSx,
  planTaskCheckboxShortSx,
  planCalendarTaskTitleFontSize,
  planDayItemShortFontSize,
  PLAN_LINE_HEIGHT_SHORT,
  PLAN_LINE_HEIGHT_TASK,
} from '@/components/ui/taskVisualTokens';
import { buildTaskRowTitleSx } from '@/components/ui/buildTaskRowTitleTrailingSx';
import { FIFTEEN_MINUTE_SLOT_HEIGHT } from '@/config/constants';
import { TaskRowProjectPillTierMeasurer } from '@/features/tasks/components/atoms/TaskRowProjectPillTierMeasurer';
import { useScheduledTaskProjectPillTier } from '@/features/planning/hooks/time-grid/useScheduledTaskProjectPillTier';
import { renderScheduledTaskProjectChip } from '@/features/planning/components/time-grid/ScheduledTaskChrome';

/**
 * @param {{
 *   task: import('@/types').Task,
 *   user: object | null,
 *   handleToggleComplete: Function,
 *   isShort: boolean,
 *   primaryProject: string | null,
 *   projectDisplayMap: Map<string, string> | null,
 * }} props
 */
export function ScheduledTaskTitleRow({
  task,
  user,
  handleToggleComplete,
  isShort,
  primaryProject,
  projectDisplayMap,
}) {
  const titleRowRef = useRef(null);
  const measureRef = useRef(null);

  const showProjectPill =
    !isShort && primaryProject && projectDisplayMap !== null;

  const displayName = useMemo(() => {
    if (!showProjectPill) return '';
    return (
      projectDisplayMap.get(primaryProject?.toLowerCase()) ??
      projectDisplayMap.get(primaryProject) ??
      primaryProject
    );
  }, [showProjectPill, primaryProject, projectDisplayMap]);

  const measureKey = displayName;
  const tier = useScheduledTaskProjectPillTier(
    titleRowRef,
    measureRef,
    showProjectPill,
    measureKey,
  );

  return (
    <Box
      ref={titleRowRef}
      data-testid="scheduled-task-title-row"
      data-title-align="center"
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: isShort ? 'stretch' : 'center',
        gap: isShort ? '2px' : '4px',
        flexGrow: isShort ? 1 : 0,
        minWidth: 0,
        width: '100%',
        minHeight: isShort ? '100%' : undefined,
      }}
    >
      <Checkbox
        checked={task.completed || false}
        tabIndex={-1}
        onChange={(e, checked) => {
          if (user) handleToggleComplete(task, checked);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        size="small"
        sx={{
          ...(isShort ? planTaskCheckboxShortSx : planTaskCheckboxSx),
          ...(!isShort ? { mt: 0, flexShrink: 0 } : {}),
          ...(isShort
            ? {
                height: FIFTEEN_MINUTE_SLOT_HEIGHT,
                minHeight: FIFTEEN_MINUTE_SLOT_HEIGHT,
                maxHeight: FIFTEEN_MINUTE_SLOT_HEIGHT,
                '& .MuiSvgIcon-root': { fontSize: 12 },
              }
            : {}),
        }}
      />
      <Box
        component="span"
        className="task-title"
        data-testid="scheduled-task-title"
        sx={{
          ...buildTaskRowTitleSx(),
          lineHeight: isShort ? PLAN_LINE_HEIGHT_SHORT : PLAN_LINE_HEIGHT_TASK,
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          fontSize: isShort
            ? planDayItemShortFontSize
            : planCalendarTaskTitleFontSize,
          textDecoration: task.completed ? 'line-through' : 'none',
        }}
      >
        {task.title}
      </Box>
      {showProjectPill ? (
        <>
          <Box
            ref={measureRef}
            aria-hidden
            sx={{
              position: 'fixed',
              left: -10000,
              top: 0,
              pointerEvents: 'none',
              visibility: 'hidden',
            }}
          >
            <TaskRowProjectPillTierMeasurer
              displayNames={[displayName]}
              dense
            />
          </Box>
          {renderScheduledTaskProjectChip(
            primaryProject,
            projectDisplayMap,
            tier,
          )}
        </>
      ) : null}
    </Box>
  );
}
